import { PanelLeftCloseIcon } from "lucide-react";

import { ReviewV2Candidate } from "./ReviewV2Candidate";
import { Center } from "@/components/Center";
import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { SheetNavigation } from "@/components/SheetNavigation";
import { Text } from "@/components/Text";
import { JobOverview } from "@jobs/JobOverview";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

import { SheetScopeProvider } from "@/contexts/SheetScopeContext";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { CandidacyStatus, DashboardReviewState, Job } from "@/types";

interface ReviewV2BodyProps {
  candidacyId: string | null;
  canMoveToNext: boolean;
  canMoveToPrevious: boolean;
  isJobOverviewCollapsed: boolean;
  job: Job;
  onActioned: (payload: {
    agentActive: boolean;
    candidacyId: string;
    candidateName: string;
    status: CandidacyStatus;
  }) => void;
  onClose: () => void;
  onCollapseJobOverview: () => void;
  onMoveToNext: () => void;
  onMoveToPrevious: () => void;
  safeIndex: number;
  state: DashboardReviewState;
  total: number;
  userId: number;
}

export const ReviewV2Body = (props: ReviewV2BodyProps) => {
  const { candidacyId, job, state } = props;
  const { companyId, companyName, jobId } = state;

  /* Render */
  const render = () => {
    if (candidacyId === null) {
      return (
        <Center className="min-h-0 flex-1">
          <Text className="italic text-slate-500">
            No applications to review.
          </Text>
        </Center>
      );
    }

    return (
      // Off the job route there are no :companyId/:jobId params, so the query
      // keys inside the sheet come from here.
      <SheetScopeProvider
        candidacyId={candidacyId}
        companyId={companyId}
        companyName={companyName}
        job={job}
        jobId={jobId}
      >
        <ReviewV2BodyContent {...props} candidacyId={candidacyId} />
      </SheetScopeProvider>
    );
  };

  return render();
};

const ReviewV2BodyContent = ({
  candidacyId,
  canMoveToNext,
  canMoveToPrevious,
  isJobOverviewCollapsed,
  job,
  onActioned,
  onClose,
  onCollapseJobOverview,
  onMoveToNext,
  onMoveToPrevious,
  safeIndex,
  state,
  total,
  userId,
}: Omit<ReviewV2BodyProps, "candidacyId"> & { candidacyId: string }) => {
  const { companyName, jobTitle } = state;

  /* Render */
  const renderJobOverview = () => {
    if (isJobOverviewCollapsed) return null;

    return (
      <Column className="min-h-0 min-w-0 flex-[3] gap-2">
        <Row className="h-10 min-w-0 shrink-0 items-center gap-2">
          <Label className="truncate text-lg font-semibold tracking-tight text-slate-900">
            {companyName}
          </Label>
          <Text className="shrink-0 text-lg font-normal leading-none text-slate-400">
            ·
          </Text>
          <Label className="truncate text-lg font-semibold tracking-tight text-slate-900">
            {jobTitle}
          </Label>
        </Row>
        <Column className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <JobOverview
            className="[&_.tiptap]:font-normal [&_.tiptap]:leading-relaxed [&_.tiptap]:text-[#121212] [&_.tiptap_h3]:mb-1 [&_.tiptap_h3]:mt-4 [&_.tiptap_h3]:text-sm [&_.tiptap_h3]:font-semibold [&_.tiptap_h3]:leading-6 [&_.tiptap_h3]:text-slate-900 [&_.tiptap_h3:first-child]:mt-0 [&_.tiptap_li]:pl-1 [&_.tiptap_p]:mb-2 [&_.tiptap_ul]:space-y-1 [&_.tiptap_ul]:pl-5"
            companyName={companyName}
            headerAction={
              <Button
                className="h-8 w-8 text-slate-500"
                onClick={() => {
                  mixpanelUtils.trackButtonClick(
                    "Collapse job overview",
                    MIXPANEL_SCREENS.APPLICATION_REVIEW_PORTAL,
                  );
                  onCollapseJobOverview();
                }}
                size="icon"
                type="button"
                variant="ghost"
              >
                <PanelLeftCloseIcon className="h-4 w-4" />
              </Button>
            }
            hideActions
            job={job}
            screenName={MIXPANEL_SCREENS.CANDIDATE_SHEET}
          />
        </Column>
      </Column>
    );
  };

  const render = () => {
    return (
      <Row className="relative min-h-0 flex-1 gap-3 overflow-hidden p-3 pt-2">
        {renderJobOverview()}
        <Column className="min-h-0 min-w-0 flex-[5] gap-2">
          <Row className="h-10 shrink-0 items-center justify-between gap-2">
            <Label className="text-lg font-semibold tracking-tight text-slate-900">
              Candidate
            </Label>
            <SheetNavigation
              betweenButtons={
                <Text className="text-xs font-medium tabular-nums text-slate-600">
                  {safeIndex + 1} / {total}
                </Text>
              }
              canMoveToNext={canMoveToNext}
              canMoveToPrevious={canMoveToPrevious}
              handleMoveToNext={onMoveToNext}
              handleMoveToPrevious={onMoveToPrevious}
              screenName={MIXPANEL_SCREENS.CANDIDATE_SHEET}
              variant="outline"
            />
          </Row>
          <ReviewV2Candidate
            candidacyId={candidacyId}
            canMoveToNext={canMoveToNext}
            canMoveToPrevious={canMoveToPrevious}
            job={job}
            onActioned={onActioned}
            onClose={onClose}
            onMoveToNext={onMoveToNext}
            onMoveToPrevious={onMoveToPrevious}
            userId={userId}
          />
        </Column>
      </Row>
    );
  };

  return render();
};
